import { untrack } from "svelte";
import type { BloomreachResponse } from "~/lib/bloomreach/types";
import type { FilterState } from "~/lib/filters/types";
import type { ListingState } from "./create-listing-state.svelte";

export interface TrackListingEventsConfig {
  onPageViewed: (
    resultCount: number,
    filters: FilterState,
    sort: string,
    firstPage: BloomreachResponse | undefined,
  ) => void;
  onFiltersChanged: (filters: FilterState) => void;
  // For listings whose query can be intentionally disabled from the start (e.g. search
  // with no query typed yet) and so will never itself become isPending: false with data —
  // returning true fires onPageViewed immediately with a zeroed result instead of waiting.
  viewedImmediately?: () => boolean;
}

export function trackListingEvents(listing: ListingState, config: TrackListingEventsConfig) {
  // One-shot flags — co-located with the effects that read them.
  let pageViewedEmitted = false;
  let isFirstFilterRun = true;
  // Tracks the resetEpoch value at the last effect run to detect resets.
  let lastSeenResetEpoch = listing.resetEpoch;

  // Emit page_viewed once, on the first successful data load or once viewedImmediately()
  // says the page is already showing everything it's going to show without one — whichever
  // happens first. Never fires twice: an empty landing doesn't get a second page_viewed
  // once the visitor's first real query resolves.
  $effect(() => {
    if (pageViewedEmitted) return;
    const hasData = !listing.query.isPending && listing.query.data;
    if (!hasData && !config.viewedImmediately?.()) return;
    pageViewedEmitted = true;
    const firstPage = listing.query.data?.pages[0];
    untrack(() =>
      config.onPageViewed(
        firstPage?.response.numFound ?? 0,
        listing.activeFilters.current,
        listing.activeSort,
        firstPage,
      ),
    );
  });

  // Emit filters_changed when debounced filters settle — but skip the initial
  // mount run and the run that immediately follows a full reset.
  $effect(() => {
    const _filters = listing.activeFilters.current;
    const currentEpoch = listing.resetEpoch;
    if (isFirstFilterRun) {
      isFirstFilterRun = false;
      lastSeenResetEpoch = currentEpoch;
      return;
    }
    if (currentEpoch !== lastSeenResetEpoch) {
      lastSeenResetEpoch = currentEpoch;
      return;
    }
    untrack(() => config.onFiltersChanged(_filters));
  });
}
